import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import {
  GraduationCap,
  CalendarDays,
  CircleDollarSign,
  AlertCircle,
  FolderEdit,
  User,
  StopCircle,
  FolderArchive,
  UserRoundCheck,
  UserSearch,
} from "lucide-react";

import Block3Columns from "../../components/Block3Columns";
import ImageContainer from "../../components/ImageContainer";
import Block2ColumnsText from "../../components/Block2ColumnsText";
import MasonryGallery from "../../components/MasonryGallery";
import DataFetcherTemplateHome2 from "../../hooks/DataFetcherTemplate2";
import Modal from "../../components/Modal";

const url =
  "https://portales.utpl.edu.ec/beportales/jsonapi/node/template_home/37fb7bec-6eaa-4cf2-a8d1-47ede4827279" +
  "?include=field_imagen_portada.field_media_image,field_imagen13.field_media_image," +
  "field_imagen14.field_media_image,field_imagen15.field_media_image" +
  "&fields[node--template_home]=title,field_imagen_portada,field_texto_portada,field_bloque1,field_bloque2,field_bloque3,field_bloque4,field_bloque5,field_bloque6,field_bloque7,field_bloque8,field_bloque9,field_bloque10,field_bloque11,field_bloque12,field_bloque13,field_bloque14,field_bloque15," +
  "field_bloque16,field_bloque17,field_bloque18,field_bloque19,field_bloque20,field_bloque21,field_bloque22,field_bloque23,field_bloque24,field_bloque25,field_bloque26,field_bloque27,field_bloque28,field_bloque29,field_bloque30," +
  "field_imagen13,field_imagen14,field_imagen15" +
  "&fields[media--image]=field_media_image";

// Variantes de animación reutilizables
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

function Referidos() {
  const [activeTab, setActiveTab] = useState("carrera");

  const DataDisplay = ({ data }) => (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="w-full"
    >
      {/* Hero Section */}
      <section className="bg-[#004270] pt-24 text-center [&_h2]:text-white [&_h2_strong]:text-[#febe10] [&_p]:text-white">
        <motion.div variants={fadeInUp} className="max-w-4xl m-auto   px-6">
          <div>
            <div className="flex justify-center py-8">
              <img
                src={data.imagen_portada}
                alt="Referidos UTPL"
                className="h-20 md:h-40 w-auto object-contain"
              />
            </div>

            <div dangerouslySetInnerHTML={{ __html: data.texto_portada }} />
          </div>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-xl mx-auto px-6 py-10"
        >
          {data.bloque1 && (
            <motion.div
              variants={fadeInUp}
              whileHover={{ scale: 1.03 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="rounded-2xl bg-white/5 p-6 shadow-sm backdrop-blur-sm"
            >
              <div className="m-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white mb-6">
                <GraduationCap className="h-6 w-6 text-[#004270]" />
              </div>
              <div
                className="[&_h2]:text-sm [&_h2]:font-bold [&_h2]:m-0 [&_h2]:p-0"
                dangerouslySetInnerHTML={{ __html: data.bloque1 }}
              />
            </motion.div>
          )}

          {data.bloque2 && (
            <motion.div
              variants={fadeInUp}
              whileHover={{ scale: 1.03 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="rounded-2xl bg-white/5 p-6 shadow-sm backdrop-blur-sm"
            >
              <div className="m-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white mb-6">
                <CalendarDays className="h-6 w-6 text-[#004270]" />
              </div>
              <div
                className="[&_h2]:text-sm [&_h2]:font-bold [&_h2]:m-0 [&_h2]:p-0"
                dangerouslySetInnerHTML={{ __html: data.bloque2 }}
              />
            </motion.div>
          )}
        </motion.div>
      </section>

      {/* Incentive Block */}
      <section className="bg-[#004270] pb-24 px-6">
        <motion.div
          variants={fadeInUp}
          className="bg-white py-10 px-6 max-w-4xl m-auto rounded-xl shadow-xl"
        >
          <div className="m-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#febe10]">
            <CircleDollarSign className="h-6 w-6 text-[#004270]" />
          </div>

          <div
            className="[&_h2]:text-[#004270] [&_h2]:text-center [&_h2]:font-bold mt-4"
            dangerouslySetInnerHTML={{ __html: data.bloque3 }}
          />

          <motion.div
            whileHover={{ scale: 1.01 }}
            className="mt-6 p-6 bg-[#004270] text-center max-w-lg m-auto rounded-lg shadow-inner
              [&_h2]:text-white [&_h2]:text-lg [&_h2]:m-0 [&_h2]:p-0 [&_h2_strong]:text-lg 
              [&_h3]:text-[#febe10] [&_h3]:m-0 [&_h3]:p-0 [&_p]:text-white"
            dangerouslySetInnerHTML={{ __html: data.bloque4 }}
          />

          <div className="flex items-center justify-center gap-2 mt-4 text-center">
            <AlertCircle className="h-4 w-4 shrink-0 text-[#febe10]" />
            <div
              className="[&_p]:text-slate-600 [&_p]:text-xs py-2"
              dangerouslySetInnerHTML={{ __html: data.bloque5 }}
            />
          </div>
        </motion.div>
      </section>

      {/* Step by Step Section */}
      <section className="bg-slate-50">
        <div className="max-w-6xl mx-auto py-16   px-6   ">
          {data.bloque6 && (
            <motion.div
              variants={fadeInUp}
              className=" mb-12 text-center [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:text-[#004270] [&_p]:mt-4 [&_p]:text-slate-600 [&_p]:max-w-3xl [&_p]:mx-auto"
              dangerouslySetInnerHTML={{ __html: data.bloque6 }}
            />
          )}

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {[
              { number: "1", content: data.bloque7 },
              { number: "2", content: data.bloque8 },
              { number: "3", content: data.bloque9 },
            ].map(
              (item) =>
                item.content && (
                  <motion.div
                    key={item.number}
                    variants={fadeInUp}
                    whileHover={{ y: -6, transition: { duration: 0.2 } }}
                    className="relative rounded-2xl border border-slate-200 bg-white p-8 shadow-sm hover:shadow-lg transition-shadow"
                  >
                    <div className="absolute -top-5 left-6 flex h-10 w-10 items-center justify-center rounded-full bg-[#004270] text-lg font-bold text-white shadow-md">
                      {item.number}
                    </div>

                    <div
                      className="[&_h3]:mb-3 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-[#004270] [&_p]:text-slate-600 [&_p]:leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: item.content }}
                    />
                  </motion.div>
                ),
            )}
          </motion.div>
        </div>
      </section>
      {/* formulario */}
      <section className="text-center pt-24 pb-10 m-auto [&_iframe]:m-auto">
        {" "}
        <motion.div
          variants={fadeInUp}
          className=" mb-12 text-center [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:text-[#004270] [&_p]:mt-4 [&_p]:text-slate-600 [&_p]:max-w-3xl [&_p]:mx-auto"
          dangerouslySetInnerHTML={{ __html: data.bloque17 }}
        />
        <Modal
          title={<p>Revisar información de consentimiento</p>}
          content={<div dangerouslySetInnerHTML={{ __html: data.bloque18 }} />}
        ></Modal>
      </section>

      {/* Terminos */}
      <section className="bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 py-16">
          {data.bloque6 && (
            <motion.div
              variants={fadeInUp}
              className="mb-12 text-center [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:text-[#004270] [&_p]:mt-4 [&_p]:text-slate-600 [&_p]:max-w-3xl [&_p]:mx-auto"
              dangerouslySetInnerHTML={{ __html: data.bloque10 }}
            />
          )}

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            {[
              {
                number: "1",
                content: data.bloque11,
                icon: <CalendarDays></CalendarDays>,
              },
              {
                number: "2",
                content: data.bloque12,
                icon: <FolderEdit></FolderEdit>,
              },
              { number: "3", content: data.bloque13, icon: <StopCircle /> },
              { number: "4", content: data.bloque14, icon: <FolderArchive /> },
              {
                number: "5",
                content: data.bloque15,
                icon: <UserSearch></UserSearch>,
              },
              { number: "6", content: data.bloque16, icon: <UserRoundCheck /> },
            ].map(
              (item) =>
                item.content && (
                  <motion.div
                    key={item.number}
                    variants={fadeInUp}
                    whileHover={{ y: -6, transition: { duration: 0.2 } }}
                    className="relative rounded-2xl border border-slate-200 bg-white px-8 py-4 shadow-sm hover:shadow-lg transition-shadow"
                  >
                    <div className="flex flex-center">
                      <div className="absolute -top-5 left-6 flex h-10 w-10 items-center justify-center rounded-full bg-[#febe10] text-lg font-bold text-[#004270] shadow-md">
                        {item.icon}
                      </div>

                      <div
                        className="  [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-[#004270] [&_p]:text-slate-600 [&_p]:leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: item.content }}
                      />
                    </div>
                  </motion.div>
                ),
            )}
          </motion.div>
        </div>
      </section>
    </motion.div>
  );

  return (
    <DataFetcherTemplateHome2 url={url}>
      {(data) => (
        <AnimatePresence mode="wait">
          <DataDisplay key="data-loaded" data={data} />
        </AnimatePresence>
      )}
    </DataFetcherTemplateHome2>
  );
}

export default Referidos;
